import { useState, useRef, useCallback } from 'react';
import api from '../api';
import comprimeImmagine from '../utils/comprimeImmagine';

export default function FotoUpload({ productId, onUploaded, buttonLabel }) {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef();

  const addFiles = useCallback((newFiles) => {
    const fileArray = Array.from(newFiles).filter(f => f.type.startsWith('image/'));
    if (fileArray.length === 0) return;
    const newPreviews = [];
    fileArray.forEach((f) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        newPreviews.push(ev.target.result);
        if (newPreviews.length === fileArray.length) {
          setPreviews((prev) => [...prev, ...newPreviews]);
        }
      };
      reader.readAsDataURL(f);
    });
    setFiles((prev) => [...prev, ...fileArray]);
  }, []);

  const handleFileChange = (e) => { addFiles(e.target.files); if (inputRef.current) inputRef.current.value = ''; };
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); };
  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    const prog = {};
    for (let i = 0; i < files.length; i++) {
      prog[i] = 'uploading';
      setProgress({ ...prog });
      try {
        const compressed = await comprimeImmagine(files[i], 4);
        const formData = new FormData();
        formData.append('foto', compressed);
        await api.post(`/products/${productId}/foto`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        prog[i] = 'done';
      } catch { prog[i] = 'error'; }
      setProgress({ ...prog });
    }
    setFiles([]); setPreviews([]); setProgress({});
    setUploading(false);
    onUploaded();
  };

  const handleCancel = () => { setFiles([]); setPreviews([]); setProgress({}); };

  if (previews.length === 0) {
    return (
      <div
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
        className="w-full text-center cursor-pointer transition-all"
        style={{
          border: `2px dashed ${dragOver ? '#a78bfa' : '#2d1b69'}`,
          backgroundColor: '#160d30',
          borderRadius: 12,
          padding: 32,
          boxShadow: dragOver ? '0 0 16px rgba(167,139,250,0.2)' : 'none',
        }}>
        <input ref={inputRef} type="file" accept="image/*" multiple onChange={handleFileChange} style={{ display: 'none' }} />
        <p className="text-sm font-medium" style={{ color: dragOver ? '#a78bfa' : '#4b5563' }}>
          {buttonLabel || '📷 Trascina le foto qui oppure clicca per selezionare'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <input ref={inputRef} type="file" accept="image/*" multiple onChange={handleFileChange} style={{ display: 'none' }} />
      {/* Preview grid */}
      <div className="grid grid-cols-3 gap-2">
        {previews.map((p, i) => (
          <div key={i} className="relative group" style={{ width: 100, height: 100 }}>
            <img src={p} alt="" className="w-full h-full object-cover" style={{ borderRadius: 8, backgroundColor: '#1a1035' }} />
            {/* Order badge */}
            <span className="absolute top-1 left-1 text-white font-bold px-1.5 py-0.5 rounded"
              style={{ fontSize: '0.7rem', backgroundColor: i === 0 ? '#a78bfa' : '#7c3aed' }}>
              {i === 0 ? 'Cover' : i + 1}
            </span>
            {/* Progress overlay */}
            {progress[i] === 'uploading' && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center" style={{ borderRadius: 8 }}>
                <svg className="animate-spin h-5 w-5" style={{ color: '#a78bfa' }} viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
              </div>
            )}
            {progress[i] === 'done' && (
              <div className="absolute inset-0 flex items-center justify-center" style={{ borderRadius: 8, backgroundColor: 'rgba(74,222,128,0.2)' }}><span style={{ color: '#4ade80' }} className="text-lg">✓</span></div>
            )}
            {progress[i] === 'error' && (
              <div className="absolute inset-0 flex items-center justify-center" style={{ borderRadius: 8, backgroundColor: 'rgba(248,113,113,0.2)' }}><span style={{ color: '#f87171' }} className="text-lg">✗</span></div>
            )}
            {/* Remove button */}
            {!uploading && (
              <button onClick={() => removeFile(i)} className="absolute top-1 right-1 w-5 h-5 rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ backgroundColor: '#f87171', color: 'white' }}>✕</button>
            )}
          </div>
        ))}
        {!uploading && (
          <button onClick={() => inputRef.current?.click()}
            className="flex items-center justify-center transition-colors"
            style={{ width: 100, height: 100, borderRadius: 8, border: '2px dashed #2d1b69', color: '#4b5563' }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#a78bfa'; e.currentTarget.style.color = '#a78bfa'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#2d1b69'; e.currentTarget.style.color = '#4b5563'; }}>
            <span className="text-2xl">+</span>
          </button>
        )}
      </div>
      {/* Upload progress bar */}
      {uploading && (
        <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#160d30' }}>
          <div className="h-full rounded-full transition-all" style={{
            width: `${(Object.values(progress).filter(v => v === 'done').length / files.length) * 100}%`,
            background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
          }} />
        </div>
      )}
      {/* Buttons */}
      <div className="flex gap-2">
        <button type="button" onClick={handleUpload} disabled={uploading}
          className="flex-1 font-bold py-2 text-sm transition-opacity hover:opacity-90 disabled:opacity-50 text-white"
          style={{ borderRadius: 8, background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
          {uploading ? `Caricamento ${Object.values(progress).filter(v => v === 'done').length}/${files.length}...` : `Carica ${files.length} foto`}
        </button>
        {!uploading && (
          <button type="button" onClick={handleCancel} className="px-4 py-2 text-sm transition-colors"
            style={{ borderRadius: 8, backgroundColor: '#160d30', border: '1px solid #2d1b69', color: '#4b5563' }}>
            Annulla
          </button>
        )}
      </div>
    </div>
  );
}
